import type { FC } from 'react';
import { useRef } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Tooltip } from '../../../../components/Tooltip';
import type { ContactVariant, PrivateData, PrivateDataType } from '../../../../domain/contact';
import { translate } from '../../../../services/i18n';

import { PrivateDataErrorItemView, PrivateDataItemView } from './PrivateDataItemView';
import { StyledPrivateData } from './styles';

export type PrivateDataViewProps = {
  privateData: PrivateData;
  animatedMount?: boolean;
  variant?: ContactVariant;
  onCopyClick: (type: PrivateDataType) => void;
};

export const PrivateDataView: FC<PrivateDataViewProps> = (props) => {
  const { privateData, animatedMount = false, variant = 'listItem', onCopyClick } = props;
  const hasEmailError = privateData.revealErrors?.hasValidEmailsError;
  const hasDirectPhoneError = privateData.revealErrors?.hasDirectPhoneNumbersError;
  const hasMobileError = privateData.revealErrors?.hasMobilePhoneNumbersError;
  const mobilePhones = privateData.phoneNumbers?.filter((phoneNumber) => phoneNumber.internalType === 'mobile') || [];
  const directPhones = privateData.phoneNumbers?.filter((phoneNumber) => phoneNumber.internalType === 'direct') || [];
  const ref = useRef<HTMLDivElement>(null);

  const doNotCallInfo = (
    <Tooltip content={translate('si.contacts.dnc.tooltip.title')} placement="top">
      <SWReactIcons iconName={'block'} size="xs" iconColor={colorsPalettes.red['400_P']} />
    </Tooltip>
  );

  return (
    <StyledPrivateData data-automation="PrivateData" ref={ref}>
      {hasEmailError ? (
        <PrivateDataErrorItemView type="email" variant={variant} animatedMount={animatedMount} />
      ) : (
        privateData.emails?.map((email, i) => (
          <PrivateDataItemView
            onCopyClick={onCopyClick}
            content={email}
            type="email"
            key={i}
            variant={variant}
            animatedMount={animatedMount}
          />
        ))
      )}
      {hasDirectPhoneError ? (
        <PrivateDataErrorItemView type="direct-phone" variant={variant} animatedMount={animatedMount} />
      ) : (
        directPhones.map((phoneNumber, i) => (
          <PrivateDataItemView
            onCopyClick={onCopyClick}
            content={phoneNumber.number}
            type={`${phoneNumber.internalType}-phone`}
            key={i}
            variant={variant}
            animatedMount={animatedMount}
            info={phoneNumber.isDoNotCall ? doNotCallInfo : null}
          />
        ))
      )}
      {hasMobileError ? (
        <PrivateDataErrorItemView type="mobile-phone" variant={variant} animatedMount={animatedMount} />
      ) : (
        mobilePhones.map((phoneNumber, i) => (
          <PrivateDataItemView
            onCopyClick={onCopyClick}
            content={phoneNumber.number}
            type={`${phoneNumber.internalType}-phone`}
            key={i}
            variant={variant}
            animatedMount={animatedMount}
            info={phoneNumber.isDoNotCall ? doNotCallInfo : null}
          />
        ))
      )}
    </StyledPrivateData>
  );
};
